import React from 'react';
import { formatCentrality } from '../../../services/networkIntelligence.service';

/**
 * Card component displaying centrality metrics for a node.
 */
const CentralityMetricsCard = ({ metrics }) => {
  if (!metrics) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-3">Network Position</h3>
        <p className="text-gray-500 text-sm">No centrality data available</p>
      </div>
    );
  }

  const metricsConfig = [
    {
      key: 'degreeCentrality',
      label: 'Degree Centrality',
      description: 'Proportion of direct connections in the network',
      color: '#3B82F6',
    },
    {
      key: 'betweennessCentrality',
      label: 'Betweenness Centrality',
      description: 'How often this node lies on shortest paths between others',
      color: '#8B5CF6',
    },
    {
      key: 'pagerank',
      label: 'PageRank',
      description: 'Importance based on connections to important nodes',
      color: '#EC4899',
    },
    {
      key: 'eigenvectorCentrality',
      label: 'Eigenvector Centrality',
      description: 'Influence based on connection quality',
      color: '#F59E0B',
    },
  ];

  // Find the highest metric for highlighting
  const maxMetric = metricsConfig.reduce((max, m) => {
    const val = metrics[m.key] || 0;
    return val > (metrics[max.key] || 0) ? m : max;
  }, metricsConfig[0]);

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-semibold mb-3">Network Position</h3>

      <div className="space-y-4">
        {metricsConfig.map(metric => {
          const value = metrics[metric.key] || 0;
          const isHighest = metric.key === maxMetric.key && value > 0;

          return (
            <div key={metric.key}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-700 flex items-center">
                  {metric.label}
                  {isHighest && (
                    <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded">
                      Highest
                    </span>
                  )}
                </span>
                <span
                  className="text-sm font-semibold"
                  style={{ color: metric.color }}
                >
                  {formatCentrality(value)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(100, value * 100)}%`,
                    backgroundColor: metric.color,
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{metric.description}</p>
            </div>
          );
        })}
      </div>

      {/* Interpretation */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <div className="text-sm font-medium text-gray-700 mb-1">Interpretation</div>
        <p className="text-xs text-gray-600">
          {metrics.degreeCentrality > 0.3 && (
            'This subject has many direct connections in the network. '
          )}
          {metrics.betweennessCentrality > 0.2 && (
            'Acts as a bridge between different groups. '
          )}
          {metrics.pagerank > 0.1 && (
            'Connected to other influential individuals. '
          )}
          {metrics.degreeCentrality <= 0.1 && metrics.betweennessCentrality <= 0.05 && (
            'Peripheral position in the network with few connections. '
          )}
          {Object.values(metrics).every(v => v === 0 || v === undefined) && (
            'Not significantly connected in the network.'
          )}
        </p>
      </div>
    </div>
  );
};

export default CentralityMetricsCard;
